import {
  findAssemblyDocumentNodeByPersistedNodeId,
  getAssemblyDocumentNode,
  listAssemblyDocumentDirectChildNodes,
} from '../../project-model/assembly-document/assembly-document-operations'
import type { ModelAssetNode } from '../../project-model/assets/model-asset-types'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyProjectModelDocument,
  PuppyProjectModelExportResult,
  PuppyProjectModelExportSource,
  PuppyProjectModelOccurrence,
} from './types'
import { PUPPY_PROJECT_MODEL_VERSION } from './types'

/**
 * 项目 occurrence 文档创建输入。
 */
export interface CreatePuppyProjectModelDocumentInput {
  /** 已完成 Mate、bodyBinding 和层级归一化的装配语义文档。 */
  readonly assembly: PuppyAssemblyDocument
  /** 项目保存时使用的可选项目模型权威源。 */
  readonly projectModelSource?: PuppyProjectModelExportSource | null
}

/**
 * 判断装配节点是否是项目模型需要恢复的组件 occurrence。
 *
 * @param node 当前装配节点。
 * @param isRoot 当前节点是否为根节点。
 * @returns 根节点或 assembly/part 语义节点返回 true。
 */
function isProjectOccurrenceNode(node: PuppyAssemblyNode, isRoot: boolean): boolean {
  return isRoot || node.semanticKind === 'assembly' || node.semanticKind === 'part'
}

/**
 * 解析项目 occurrence 使用的节点类型。
 *
 * @param node 当前装配节点。
 * @param isRoot 当前节点是否为根节点。
 * @returns 可写入项目文档的 assembly/part 类型。
 */
function resolveProjectOccurrenceNodeType(
  node: PuppyAssemblyNode,
  isRoot: boolean,
): PuppyProjectModelOccurrence['nodeType'] {
  if (node.semanticKind === 'part') {
    return 'part'
  }
  if (node.semanticKind === 'assembly') {
    return 'assembly'
  }
  if (isRoot) {
    return 'assembly'
  }
  throw new Error(`PuppyCAD 项目 occurrence 缺少组件语义：assemblyNodeId=${node.nodeId}`)
}

/**
 * 创建子 occurrence 的文档内身份。
 *
 * @param parentOccurrenceId 父 occurrence ID。
 * @param assemblyNodeId 当前装配节点 ID。
 * @returns 不依赖运行期 projectNodeKey 的文档内稳定路径。
 */
function createChildOccurrenceId(parentOccurrenceId: string, assemblyNodeId: string): string {
  return `${parentOccurrenceId}/${encodeURIComponent(assemblyNodeId)}`
}

/**
 * 从 assembly.json 建立包本地项目 occurrence 列表。
 *
 * 非组件中间层不会进入项目文档，但其 assembly/part 后代仍会连接到最近的项目父 occurrence，
 * 保证显示承载层不会污染可编辑结构。
 *
 * @param assembly 装配语义文档。
 * @returns 按 assembly 前序排列的 occurrence 列表。
 */
function createPackageLocalOccurrences(
  assembly: PuppyAssemblyDocument,
): PuppyProjectModelOccurrence[] {
  const nodeById = new Map(assembly.nodes.map((node) => [node.nodeId, node]))
  const rootNode = nodeById.get(assembly.rootNodeId)
  if (!rootNode) {
    throw new Error(`PuppyCAD 项目 occurrence 缺少装配根节点：${assembly.rootNodeId}`)
  }

  const occurrences: PuppyProjectModelOccurrence[] = []
  const visit = (
    node: PuppyAssemblyNode,
    parentOccurrenceId: string | null,
    isRoot: boolean,
  ): void => {
    const shouldCreateOccurrence = isProjectOccurrenceNode(node, isRoot)
    const occurrenceId = isRoot
      ? 'root'
      : shouldCreateOccurrence && parentOccurrenceId
        ? createChildOccurrenceId(parentOccurrenceId, node.nodeId)
        : parentOccurrenceId

    if (shouldCreateOccurrence) {
      if (!occurrenceId) {
        throw new Error(`PuppyCAD 项目 occurrence 缺少父身份：assemblyNodeId=${node.nodeId}`)
      }
      occurrences.push({
        occurrenceId,
        parentOccurrenceId,
        assemblyNodeId: node.nodeId,
        name: node.name,
        nodeType: resolveProjectOccurrenceNodeType(node, isRoot),
      })
    }

    const nextParentOccurrenceId = shouldCreateOccurrence ? occurrenceId : parentOccurrenceId
    const childNodeIds = assembly.childIdsByNodeId[node.nodeId] ?? []
    childNodeIds.forEach((childNodeId) => {
      const childNode = nodeById.get(childNodeId)
      if (!childNode) {
        throw new Error(`PuppyCAD 项目 occurrence 引用未知装配节点：${node.nodeId}/${childNodeId}`)
      }
      visit(childNode, nextParentOccurrenceId, false)
    })
  }

  visit(rootNode, null, true)
  return occurrences
}

/**
 * 解析项目节点在最终 assembly.json 中的唯一装配节点。
 *
 * @param input 项目 nodeKey、统一运行时状态和装配节点索引。
 * @returns 唯一命中的装配节点。
 */
function resolveProjectAssemblyNode(input: {
  readonly nodeKey: string
  readonly source: PuppyProjectModelExportSource
  readonly assembly: PuppyAssemblyDocument
}): PuppyAssemblyNode {
  const runtimeBinding = input.source.loadResult.runtimeState.bindingsByNodeKey.get(input.nodeKey)
  if (!runtimeBinding) {
    throw new Error(
      `PuppyCAD 项目节点缺少运行时装配绑定：nodeKey=${input.nodeKey}`,
    )
  }
  const assemblyNode = input.assembly.nodes.find(
    node => node.nodeId === runtimeBinding.runtimeAssemblyNodeId,
  )
  if (!assemblyNode) {
    throw new Error(
      'PuppyCAD 项目节点运行时装配身份不在最终 assembly.json：'
      + `nodeKey=${input.nodeKey}, assemblyNodeId=${runtimeBinding.runtimeAssemblyNodeId}`,
    )
  }
  return assemblyNode
}

/**
 * 解析项目模型节点在当前工作装配文档中的明确来源节点。
 *
 * 离线 `.puppy` 重载会从 occurrenceId 派生新的运行时 nodeKey，该 key 不再等于后端工作文档
 * 的结构路径。complete finalization 已经把两者的唯一关系写入 runtime binding，导出必须消费
 * 该关系；`persistedNodeId` 可以被多个 occurrence 共享，只保留给普通历史节点兼容查找。
 *
 * @param source 当前项目模型与工作装配文档。
 * @param modelNode 待导出的项目模型节点。
 * @returns 明确命中的工作文档节点；包内原生 occurrence 返回 null。
 */
function resolveProjectAssemblyDocumentNode(
  source: PuppyProjectModelExportSource,
  modelNode: ModelAssetNode,
): ReturnType<typeof getAssemblyDocumentNode> {
  const directNode = getAssemblyDocumentNode(source.assemblyDocument, modelNode.nodeKey)
  if (directNode) {
    return directNode
  }

  const runtimeBinding = source.loadResult.runtimeState.bindingsByNodeKey.get(modelNode.nodeKey)
  const assemblyDocumentNodeKey = runtimeBinding?.assemblyDocumentNodeKey?.trim() ?? ''
  if (assemblyDocumentNodeKey) {
    const boundNode = getAssemblyDocumentNode(source.assemblyDocument, assemblyDocumentNodeKey)
    if (!boundNode) {
      throw new Error(
        'PuppyCAD 项目 occurrence 的工作装配绑定已失效：'
        + `nodeKey=${modelNode.nodeKey}, assemblyDocumentNodeKey=${assemblyDocumentNodeKey}`,
      )
    }
    return boundNode
  }

  if (modelNode.assetSource?.kind === 'embedded-puppy-occurrence') {
    return null
  }
  return modelNode.persistedNodeId
    ? findAssemblyDocumentNodeByPersistedNodeId(
        source.assemblyDocument,
        modelNode.persistedNodeId,
      )
    : null
}

/**
 * 使用当前项目模型权威源创建 occurrence 图。
 *
 * @param assembly 最终装配文档。
 * @param source 当前项目模型和装配工作文档。
 * @returns 与真实 ModelAssetNode 树一一对应的 occurrence 文档和外部根直系 provenance。
 */
function createProjectOccurrencesFromSource(
  assembly: PuppyAssemblyDocument,
  source: PuppyProjectModelExportSource,
): PuppyProjectModelExportResult {
  const occurrences: PuppyProjectModelOccurrence[] = []
  const rootDirectOccurrenceIdentities: PuppyProjectModelExportResult['rootDirectOccurrenceIdentities'][number][] = []
  const usedAssemblyNodeIds = new Set<string>()
  const visitedNodeKeys = new Set<string>()
  // package-local 根直系 occurrence 也进入项目文档，但只有精确命中外部 direct child nodeKey 的节点可参与仓库桥接。
  const externalRootDirectNodeKeys = new Set(
    listAssemblyDocumentDirectChildNodes(source.assemblyDocument, source.loadResult.root.nodeKey)
      .map((node) => node.nodeKey),
  )
  const appendOccurrence = (
    nodeKey: string,
    modelNode: ModelAssetNode | null,
    assemblyDocumentNode: ReturnType<typeof getAssemblyDocumentNode>,
    parentOccurrenceId: string | null,
    isRoot: boolean,
    isRootDirect: boolean,
    visitModelChildren: boolean,
  ): void => {
    const assemblyNode = resolveProjectAssemblyNode({
      nodeKey,
      source,
      assembly,
    })
    if (usedAssemblyNodeIds.has(assemblyNode.nodeId)) {
      throw new Error(
        `PuppyCAD 多个项目节点绑定到同一装配节点：nodeKey=${nodeKey}, assemblyNodeId=${assemblyNode.nodeId}`,
      )
    }
    usedAssemblyNodeIds.add(assemblyNode.nodeId)
    visitedNodeKeys.add(nodeKey)

    const occurrenceId = isRoot
      ? 'root'
      : parentOccurrenceId
        ? createChildOccurrenceId(parentOccurrenceId, assemblyNode.nodeId)
        : ''
    if (!occurrenceId) {
      throw new Error(`PuppyCAD 项目节点缺少父 occurrence：nodeKey=${nodeKey}`)
    }
    const materializedInstanceId = modelNode?.instanceUuid?.trim()
    // 历史离线包缺少 instanceId 时，物化层会暂以 occurrenceId 填充 instanceUuid；
    // 一旦该 occurrence 已绑定工作装配文档，必须写回文档中的真实实例身份。
    const instanceId = modelNode?.assetSource?.kind === 'embedded-puppy-occurrence'
      ? assemblyDocumentNode?.instanceId?.trim() || materializedInstanceId
      : materializedInstanceId || assemblyDocumentNode?.instanceId?.trim()
    const repositoryId = modelNode?.repositoryId.trim() || assemblyDocumentNode?.repositoryId.trim()
    const branchId = modelNode?.branchId.trim() || assemblyDocumentNode?.branchId?.trim()
    const commitId = modelNode?.commitId.trim() || assemblyDocumentNode?.commitId.trim()
    occurrences.push({
      occurrenceId,
      parentOccurrenceId,
      assemblyNodeId: assemblyNode.nodeId,
      ...(assemblyDocumentNode?.persistedNodeId
        ? { persistedNodeId: assemblyDocumentNode.persistedNodeId }
        : {}),
      ...(instanceId ? { instanceId } : {}),
      name: modelNode?.displayName?.trim()
        || assemblyDocumentNode?.instanceName?.trim()
        || assemblyNode.name,
      nodeType: modelNode?.nodeType ?? assemblyDocumentNode?.nodeType ?? resolveProjectOccurrenceNodeType(assemblyNode, isRoot),
      ...(!isRoot && repositoryId && commitId
        ? {
            versionReference: {
              repositoryId,
              ...(branchId ? { branchId } : {}),
              commitId,
            },
          }
        : {}),
    })
    const provenanceNodeKey = assemblyDocumentNode?.nodeKey ?? nodeKey
    if (isRootDirect && externalRootDirectNodeKeys.has(provenanceNodeKey)) {
      rootDirectOccurrenceIdentities.push({
        sourceNodeKey: provenanceNodeKey,
        assemblyNodeId: assemblyNode.nodeId,
      })
    }
    if (!visitModelChildren) {
      return
    }
    modelNode?.children.forEach((child) => {
      const childDocumentNode = resolveProjectAssemblyDocumentNode(source, child)
      appendOccurrence(child.nodeKey, child, childDocumentNode, occurrenceId, false, isRoot, true)
    })
  }

  const rootDocumentNode = getAssemblyDocumentNode(
    source.assemblyDocument,
    source.loadResult.root.nodeKey,
  )
  appendOccurrence(
    source.loadResult.root.nodeKey,
    source.loadResult.root,
    rootDocumentNode,
    null,
    true,
    false,
    false,
  )

  /**
   * complete GLB 可以承载未物化到 ModelAssetNode.children 的根直系 occurrence。
   * 工作装配文档决定应提交哪些节点，统一 runtimeState 决定这些节点对应哪个最终 assemblyNodeId。
   */
  const directDocumentNodes = listAssemblyDocumentDirectChildNodes(
    source.assemblyDocument,
    source.loadResult.root.nodeKey,
  )
  const modelNodeByDocumentNodeKey = new Map<string, ModelAssetNode>()
  source.loadResult.root.children.forEach((modelNode) => {
    const documentNode = resolveProjectAssemblyDocumentNode(source, modelNode)
    if (documentNode) {
      modelNodeByDocumentNodeKey.set(documentNode.nodeKey, modelNode)
    }
  })

  // 根直系项目 occurrence 严格沿用工作装配顺序；complete 覆盖节点只是在该顺序中没有 ModelAssetNode。
  directDocumentNodes.forEach((documentNode) => {
    const modelNode = modelNodeByDocumentNodeKey.get(documentNode.nodeKey) ?? null
    appendOccurrence(
      modelNode?.nodeKey ?? documentNode.nodeKey,
      modelNode,
      documentNode,
      'root',
      false,
      true,
      true,
    )
  })

  // 包内原生 occurrence 可能不在外层工作装配文档中，仍按显式模型树顺序保留。
  source.loadResult.root.children.forEach((modelNode) => {
    if (!visitedNodeKeys.has(modelNode.nodeKey)) {
      appendOccurrence(modelNode.nodeKey, modelNode, null, 'root', false, true, true)
    }
  })
  return {
    document: {
      version: PUPPY_PROJECT_MODEL_VERSION,
      rootOccurrenceId: 'root',
      occurrences,
    },
    rootDirectOccurrenceIdentities,
  }
}

/**
 * 创建离线包项目 occurrence 文档及同次导出 provenance。
 *
 * @param input 最终装配语义和可选项目权威源。
 * @returns ZIP 文档与只存在于内存的根直系 occurrence 身份。
 */
export function createPuppyProjectModelExportResult(
  input: CreatePuppyProjectModelDocumentInput,
): PuppyProjectModelExportResult {
  if (input.projectModelSource) {
    return createProjectOccurrencesFromSource(
      input.assembly,
      input.projectModelSource,
    )
  }

  return {
    document: {
      version: PUPPY_PROJECT_MODEL_VERSION,
      rootOccurrenceId: 'root',
      occurrences: createPackageLocalOccurrences(input.assembly),
    },
    rootDirectOccurrenceIdentities: [],
  }
}

/**
 * 创建离线包可编辑项目 occurrence 文档。
 *
 * @param input 最终装配语义和可选项目权威源。
 * @returns 可独立校验和重建项目逻辑树的文档。
 */
export function createPuppyProjectModelDocument(
  input: CreatePuppyProjectModelDocumentInput,
): PuppyProjectModelDocument {
  return createPuppyProjectModelExportResult(input).document
}
